import type { Prisma } from '@prisma/client'
import { Link, useSearchParams } from '@remix-run/react'
import { ExternalLink, Microscope } from 'lucide-react'
import Highlighter from 'react-highlight-words'

import { getPdbFileSrc, getPmhcImgSrc } from '#app/utils/misc'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from './ui/accordion'
import { Badge } from './ui/badge'

type PmhcWithImageAndPdb = Prisma.PmhcGetPayload<{
	include: {
		image: {
			select: {
				id: true
				altText: true
			}
		}
		pdb_file: {
			select: {
				id: true
			}
		}
	}
}>

export function ListOfPmhcResults({ data }: { data: PmhcWithImageAndPdb[] }) {
	const [searchParams] = useSearchParams()
	const search = searchParams.get('search')

	if (data.length === 0) {
		return (
			<div className="text-center text-slate-500">
				No results found, try different filters or search for a different
				peptide sequence
			</div>
		)
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold">Results</h2>
				<div className="text-sm text-slate-500">
					Showing {data.length} results
				</div>
			</div>

			<Accordion type="single" collapsible className="w-full">
				{data.map(item => (
					<AccordionItem
						key={item.sequence}
						value={`item-${item.sequence}`}
						className="border rounded-lg mb-3 bg-white"
					>
						<AccordionTrigger className="px-4 hover:no-underline">
							<div className="flex flex-col sm:flex-row sm:items-center justify-between w-full text-left">
								<div className="flex items-center gap-2">
									<Microscope className="h-4 w-4 text-teal-600" />
									<span className="font-medium">
										<Highlighter
											searchWords={[search ?? '']}
											textToHighlight={item.sequence}
										/>
									</span>
								</div>
								<div className="flex gap-2 mt-2 sm:mt-0">
									<Badge variant="outline" className="bg-slate-50">
										<Highlighter
											searchWords={[search ?? '']}
											textToHighlight={item.mhc_allele_id}
										/>
									</Badge>
									<Badge variant="outline" className="bg-slate-50">
										<Highlighter
											searchWords={[search ?? '']}
											textToHighlight={item.source_organism}
										/>
									</Badge>
								</div>
							</div>
						</AccordionTrigger>
						<AccordionContent className="px-4 pb-4">
							<div className="space-y-3">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex justify-center items-center">
										<div className="max-w-80 max-h-80 aspect-square bg-slate-100 rounded-md overflow-hidden flex items-center justify-center">
											<img
												src={getPmhcImgSrc(item.image?.id ?? '')}
												alt={item.image?.altText ?? ''}
												className="w-[150%] h-[150%] object-cover object-center"
											/>
										</div>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-1 items-center gap-4">
										<div>
											<h4 className="text-sm font-medium text-slate-500 mb-1">
												Metadata
											</h4>
											<div className="text-sm space-y-1">
												<div className="flex justify-between">
													<span className="text-slate-500">MHC Allele:</span>
													<span className="text-right">
														<Highlighter
															searchWords={[search ?? '']}
															textToHighlight={item.mhc_allele_id ?? ''}
														/>
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">
														Peptide Length:
													</span>
													<span className="text-right">
														{item.peptide_length}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">
														Immunological Background:
													</span>
													<span className="text-right">
														<Highlighter
															searchWords={[search ?? '']}
															textToHighlight={
																item.immunological_background_id ?? ''
															}
														/>
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">
														Source Protein:
													</span>
													<span className="text-right">
														<Link
															to={item.link_para_source_protein}
															target="_blank"
															className="flex items-center gap-1"
														>
															<Highlighter
																searchWords={[search ?? '']}
																textToHighlight={item.source_protein ?? ''}
															/>
															<ExternalLink className="w-4 h-4" />
														</Link>
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">
														Structure Type:
													</span>
													<span className="text-right">
														{item.link_para_structure_type ? (
															<Link
																to={item.link_para_structure_type}
																target="_blank"
																className="flex items-center gap-1"
															>
																<Highlighter
																	searchWords={[search ?? '']}
																	textToHighlight={item.structure_type_id ?? ''}
																/>
																<ExternalLink className="w-4 h-4" />
															</Link>
														) : (
															item.structure_type_id
														)}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">
														Source Organism:
													</span>
													<span className="text-right">
														<Highlighter
															searchWords={[search ?? '']}
															textToHighlight={item.source_organism ?? ''}
														/>
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">Reference:</span>
													<span className="text-right">
														{item.link_para_reference ? (
															<Link
																to={item.link_para_reference}
																target="_blank"
																className="flex items-center gap-1"
															>
																<Highlighter
																	searchWords={[search ?? '']}
																	textToHighlight={item.reference ?? ''}
																/>
																<ExternalLink className="w-4 h-4" />
															</Link>
														) : (
															item.reference
														)}
													</span>
												</div>
												<div className="flex justify-between">
													<span className="text-slate-500">IEDB id:</span>
													<span className="text-right">
														{item.link_epitope_id_by_iedb ? (
															<Link
																to={item.link_epitope_id_by_iedb}
																target="_blank"
																className="flex items-center gap-1"
															>
																{item.epitope_id_by_iedb}
																<ExternalLink className="w-4 h-4" />
															</Link>
														) : (
															item.epitope_id_by_iedb
														)}
													</span>
												</div>
											</div>
										</div>
									</div>
									<div className="flex md:col-span-2 justify-end gap-2 mt-4">
										<Link
											to={item.link_para_reference}
											className="inline-block rounded-md bg-blue-500 px-4 py-2 text-white"
											target="_blank"
										>
											Go to reference article
										</Link>
										{item.pdb_file ? (
											<Link
												to={getPdbFileSrc(item.pdb_file.id)}
												reloadDocument
												download={`${item.sequence}.pdb`}
												className="inline-block rounded-md bg-blue-500 px-4 py-2 text-white"
											>
												Download PDB file
											</Link>
										) : null}
									</div>
								</div>
							</div>
						</AccordionContent>
					</AccordionItem>
				))}
			</Accordion>
		</div>
	)
}
